/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { LoadingSpinner } from "@/shared/components/ui/loading-spinner";
import { Check, Mail } from "lucide-react";
import { useTranslation } from "react-i18next";
import SignUpService from "../services/SignUpService";
import { getEnvLocalizedUrl } from "@/shared/utils/urlLocalization";
import { analyticsService } from "@/shared/services/analytics";
import { useAuth } from "../state/AuthContext";
import { Icons } from "@/shared/components/icons/icons";

const createSignUpSchema = (t: any) => {
  return z.object({
    email: z
      .string()
      .min(1, t("auth.signup.validation.emailRequired"))
      .email(t("auth.signup.validation.emailInvalid")),
  });
};

export default function SignUp() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isMicrosoftLoading, setIsMicrosoftLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<{
    message: string;
    code?: string;
    actions?: Array<{ label: string; url: string }>;
  } | null>(null);

  const signUpSchema = createSignUpSchema(t);
  
  // Check if social sign-in is disabled via environment variable
  const isSocialSignInDisabled = 
    ((window as any).ENV?.VITE_DISABLE_SOCIAL_SIGNIN && (window as any).ENV.VITE_DISABLE_SOCIAL_SIGNIN !== '%%VITE_DISABLE_SOCIAL_SIGNIN%%')
      ? (window as any).ENV.VITE_DISABLE_SOCIAL_SIGNIN === 'true'
      : import.meta.env.VITE_DISABLE_SOCIAL_SIGNIN === 'true';

  const form = useForm<z.infer<typeof signUpSchema>>({
    resolver: zodResolver(signUpSchema),
    defaultValues: {
      email: "",
    },
  });

  // Track signup started (apenas uma vez)
  const hasTrackedStart = useRef(false);
  useEffect(() => {
    if (!hasTrackedStart.current) {
      analyticsService.trackEvent('signup_started', {});
      hasTrackedStart.current = true;
    }
  }, []);

  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true);
    // Aguardar um momento para garantir que o React renderize o spinner
    // antes de chamar signIn que fará o redirect
    await new Promise(resolve => setTimeout(resolve, 50));
    try {
      await signIn("google");
    } catch (error: any) {
      console.error("Google sign in error:", error);
      setIsGoogleLoading(false);
    }
    // Não resetar loading aqui porque o redirect acontece
  };

  const handleMicrosoftSignIn = async () => {
    setIsMicrosoftLoading(true);
    // Aguardar um momento para garantir que o React renderize o spinner
    // antes de chamar signIn que fará o redirect
    await new Promise(resolve => setTimeout(resolve, 50));
    try {
      await signIn("azure");
    } catch (error: any) {
      console.error("Microsoft sign in error:", error);
      setIsMicrosoftLoading(false);
    }
    // Não resetar loading aqui porque o redirect acontece
  };

  const onSubmit = async (values: z.infer<typeof signUpSchema>) => {
    setIsLoading(true);
    setError(null);

    try {
      // Converter idioma do i18n para formato de locale (pt → pt-BR, en → en-US)
      const currentLang = i18n.language.split('-')[0];
      const locale = currentLang === 'pt' ? 'pt-BR' : 'en-US';
      
      const response = await SignUpService.signUp(values.email, locale);

      if (response.success) {
        setSuccess(true);
        
        // Track successful signup
        analyticsService.trackEvent('signup_completed', {
          signup_method: 'email'
        });
      } else {
        setError({
          message: response.message,
          code: response.code,
          actions: response.actions,
        });
        
        // Track failed signup
        analyticsService.trackEvent('signup_failed', {
          signup_method: 'email',
          error_type: response.code,
          error_message: response.message
        });
      }
    } catch (err: any) {
      const errorMessage = err.message || t("auth.signup.errors.generic");
      
      setError({
        message: errorMessage,
      });
      
      // Track failed signup
      analyticsService.trackEvent('signup_failed', {
        signup_method: 'email',
        error_message: errorMessage
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4 relative">
        <div className="w-full max-w-md space-y-8 animate-fade-in">
          <div className="flex justify-center">
            <img 
              src="/assets/logo.png" 
              alt="Prodgy Logo" 
              className="h-32 w-auto mb-2"
            />
          </div>
          <Card className="w-full shadow-lg">
            <CardHeader className="space-y-3 pb-4">
              <CardTitle className="text-2xl font-medium tracking-tight text-center">
                {t("auth.signup.success.title")}
              </CardTitle>
              <CardDescription className="text-center text-muted-foreground">
                {t("auth.signup.success.description")}
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col items-center justify-center text-center py-6">
              <div className="mx-auto mb-6 relative">
                <div className="h-20 w-20 rounded-full bg-green-100 flex items-center justify-center">
                  <div className="h-16 w-16 rounded-full bg-green-200 flex items-center justify-center">
                    <div className="h-12 w-12 rounded-full bg-green-500 flex items-center justify-center animate-pulse">
                      <Check className="h-8 w-8 text-white" strokeWidth={3} />
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-6">
                {t("auth.signup.success.message")}
              </p>
            </CardContent>
            <CardFooter className="flex flex-col space-y-4 pt-2">
              <Button 
                variant="default" 
                className="w-full font-normal h-11" 
                onClick={() => navigate("/login")}
              >
                {t("auth.signup.buttons.backToLogin")}
              </Button>
            </CardFooter>
          </Card>
        </div>
        
        <div className="absolute bottom-4 right-4">
          <span className="text-sm text-muted-foreground opacity-80">
            {t("auth.login.poweredBy")} <a 
              href="https://programmers.com.br" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="hover:text-muted-foreground/80 transition-colors"
            >
              Programmers
            </a>®
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 relative">
      <div className="w-full max-w-md space-y-8 animate-fade-in">
        <div className="flex justify-center">
          <img 
            src="/assets/logo.png" 
            alt="Prodgy Logo" 
            className="h-32 w-auto mb-2"
          />
        </div>
        <Card className="w-full shadow-lg">
          <CardHeader className="space-y-3 pb-8">
            <CardTitle className="text-2xl font-medium tracking-tight text-center">
              {t("auth.signup.title")}
            </CardTitle>
            <CardDescription className="text-center text-muted-foreground">
              {t("auth.signup.description")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-primary/10 p-4 rounded-lg mb-6 flex items-center space-x-3">
              <div className="bg-primary/20 p-2 rounded-full">
                <Mail className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm font-medium mb-1">{t("auth.signup.info.title")}</p>
                <p className="text-xs text-muted-foreground">
                  {t("auth.signup.info.description")}
                </p>
              </div>
            </div>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("auth.signup.fields.email")}</FormLabel>
                      <FormControl>
                        <Input 
                          type="email" 
                          placeholder={t("auth.signup.fields.emailPlaceholder")}
                          {...field} 
                          className={`h-11 ${error ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                          disabled={isLoading}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="pt-2">
                  <Button 
                    type="submit" 
                    className="w-full h-11 font-normal relative" 
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center">
                        <LoadingSpinner className="h-5 w-5 text-white" size="sm" />
                      </div>
                    ) : (
                      t("auth.signup.buttons.continue")
                    )}
                  </Button>
                </div>

                {!isSocialSignInDisabled && (
                  <>
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-background px-2 text-muted-foreground">
                          {t("auth.signup.orContinueWith")}
                        </span>
                      </div>
                    </div>

                    <div className="grid gap-3">
                      <Button
                        variant="outline"
                        className="w-full font-normal h-11"
                        onClick={handleGoogleSignIn}
                        disabled={isLoading || isGoogleLoading || isMicrosoftLoading}
                        type="button"
                      >
                        {isGoogleLoading ? (
                          <LoadingSpinner className="h-5 w-5" size="sm" />
                        ) : (
                          <>
                            <Icons.google className="mr-2 h-5 w-5" />
                            {t("auth.signup.continueWithGoogle")}
                          </>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        className="w-full font-normal h-11"
                        onClick={handleMicrosoftSignIn}
                        disabled={isLoading || isGoogleLoading || isMicrosoftLoading}
                        type="button"
                      >
                        {isMicrosoftLoading ? (
                          <LoadingSpinner className="h-5 w-5" size="sm" />
                        ) : (
                          <>
                            <Icons.microsoft className="mr-2 h-5 w-5" />
                            {t("auth.signup.continueWithMicrosoft")}
                          </>
                        )}
                      </Button>
                    </div>
                  </>
                )}

                <div className="pt-4 text-center text-sm">
                  <span className="text-muted-foreground">
                    {t("auth.signup.alreadyHaveAccount")}{" "}
                  </span>
                  <Link 
                    to="/login"
                    className="text-primary hover:underline font-medium"
                  >
                    {t("auth.signup.signIn")}
                  </Link>
                </div>

                <div className="pt-2 text-xs text-center">
                  <p className="text-muted-foreground">
                    {t("auth.signup.agreeTermsStart")}{' '}
                    <a 
                      href={getEnvLocalizedUrl('VITE_PRODGY_TERMS_URL', i18n.language)} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      {t("auth.signup.termsOfService")}
                    </a>{' '}{t("auth.signup.agreeTermsMiddle")}{' '}
                    <a 
                      href={getEnvLocalizedUrl('VITE_PRODGY_PRIVACY_URL', i18n.language)} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      {t("auth.signup.privacyPolicy")}
                    </a>{t("auth.signup.agreeTermsEnd")}
                  </p>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
        
        {/* Mensagem de erro */}
        <div className={`text-xs text-center mt-2 h-4 transition-opacity ${
          error ? 'text-destructive opacity-100' : 'opacity-0'
        }`}>
          {t("auth.signup.errors.userExistsInline")}
        </div>
      </div>
      
      <div className="absolute bottom-4 right-4">
        <span className="text-sm text-muted-foreground opacity-80">
          {t("auth.login.poweredBy")} <a 
            href="https://programmers.com.br" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="hover:text-muted-foreground/80 transition-colors"
          >
            Programmers
          </a>®
        </span>
      </div>
    </div>
  );
}
